import React, { useState, useEffect, useRef } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import ConfirmModal from "../Shared/ConfirmModal";
import { useNotification } from "../../context/NotificationContext";
import { Spinner } from "../Shared/Spinner";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import useApiToken from "../../hooks/useApiToken";
import { GetInterviewSlotsByMonth } from "../../api/roles.api";

const localizer = momentLocalizer(moment);
moment.updateLocale("en", {
  week: {
    dow: 1,
  },
});
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  candidateId: string | null;
  meetingLink: string | null;
  roleId: string | null;
  roleName: string | null;
  statusId: string | null;
  status: string | null;
  candidateName: string | null;
  interviewerEmail: string | null;
}

const InterviewCalendar = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [intialDate, setInitialDate] = useState<Date>(new Date());
  const lastLoadedMonthRef = useRef<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>();
  const [stages, setStages] = useState<string[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>();
  const [interviewerEmails, setInterviewerEmails] = useState<string[]>([]);
  const [selectedInterviewerEmail, setSelectedInterviewerEmail] =
    useState<string>();
  const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([]);
  const [confirmation, setConfirmation] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const { info } = useNotification();
  const navigate = useNavigate();
  const token = useApiToken();
  useEffect(() => {
    if (!token) return;
    const now = moment();
    fetchSlotsByMonth(now.year(), now.month() + 1);
    setInitialDate(new Date(now.year(), now.month(), 1));
  }, [token]);
  useEffect(() => {
    setIsLoading(true);

    setFilteredEvents(
      events.filter((event) => {
        const eventEmails = (event.interviewerEmail || "")
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean);

        return (
          (!selectedRole || event.roleName === selectedRole) &&
          (!selectedStage || event.status === selectedStage) &&
          (!selectedInterviewerEmail ||
            eventEmails.includes(selectedInterviewerEmail))
        );
      })
    );

    setIsLoading(false);
  }, [selectedRole, selectedStage, selectedInterviewerEmail]);
  useEffect(() => {
    setIsLoading(true);
    setSelectedRole("");
    setSelectedStage("");
    setSelectedInterviewerEmail("");
    setFilteredEvents(
      events.filter((event) => {
        return (
          (!selectedRole || event.roleName === selectedRole) &&
          (!selectedStage || event.status === selectedStage) &&
          (!selectedInterviewerEmail ||
            event.interviewerEmail === selectedInterviewerEmail)
        );
      })
    );
    setIsLoading(false);
  }, [roles, stages, interviewerEmails]);

  const fetchSlotsByMonth = async (year: number, month: number) => {
    setIsLoading(true);
    try {
      if (!token) return;
      const slots = await GetInterviewSlotsByMonth(token, year, month, null);
      const mappedEvents = slots.map((slot) => ({
        id: slot.id,
        title: slot.candidateId
          ? `Interview with ${slot.candidate?.firstName} for ${slot.role?.title}`
          : `Open Slot for ${slot.role?.title}`,
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        candidateId: slot.candidateId,
        meetingLink: slot.meetingLink,
        roleId: slot.roleId,
        roleName: slot.role.title,
        statusId: slot.roleStatusId,
        status: slot.roleStatus.name,
        interviewerEmail: slot.interviewerEmail,
        candidateName: slot.candidate
          ? `${slot.candidate.firstName} ${slot.candidate.lastName}`
          : null,
      }));
      setEvents((prev) => {
        // remove duplicates (if same id appears)
        const merged = [
          ...prev.filter((p) => !mappedEvents.some((m) => m.id === p.id)),
          ...mappedEvents,
        ];
        return merged;
      });
      setRoles([...new Set(mappedEvents.map((e) => e.roleName))]);
      setStages([...new Set(mappedEvents.map((e) => e.status))]);
      const individualEmails = mappedEvents
        .flatMap((e) => (e.interviewerEmail || "").split(","))
        .map((email) => email.trim())
        .filter(Boolean); 

      const uniqueSortedEmails = [...new Set(individualEmails)].sort();

      setInterviewerEmails(uniqueSortedEmails);
      setFilteredEvents(
        mappedEvents.filter((event) => {
          return (
            (!selectedRole || event.roleName === selectedRole) &&
            (!selectedStage || event.status === selectedStage) &&
            (!selectedInterviewerEmail ||
              event.interviewerEmail === selectedInterviewerEmail)
          );
        })
      );
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };
  const handleRangeChange = (range: any) => {
    let startDate: Date | null = null;
    if (Array.isArray(range) && range.length) {
      startDate = range[0];
    } else if (range && range.start) {
      startDate = range.start;
    }
    if (!startDate) return;
    const m = moment(startDate);

    const key = `${m.year()}-${m.month() + 1}`;
    if (lastLoadedMonthRef.current === key) return;
    lastLoadedMonthRef.current = key;

    fetchSlotsByMonth(m.year(), m.month() + 1);
  };

  const handleEventClick = (event: CalendarEvent) => {
    if (event.meetingLink) {
      setSelectedEvent(event);
      setConfirmation(true);
    } else {
      info("No Meeting Link", "No meeting link is assigned to this slot.");
    }
  };

  const handleJoinMeeting = () => {
    if (selectedEvent?.meetingLink) {
      window.open(
        selectedEvent.meetingLink,
        "_blank",
        "width=800,height=800,left=800,top=0"
      );
    }

    // The dedicated slot page was removed - interview details and scoring now live on
    // the candidate profile itself, so jump there when a candidate is actually booked.
    if (selectedEvent?.roleId && selectedEvent?.candidateId) {
      navigate(`/roles/${selectedEvent.roleId}/candidates/${selectedEvent.candidateId}`);
    }
    setConfirmation(false);
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const bg = event.candidateId ? "#10b981" : "#6366f1";
    const bgHover = event.candidateId ? "#059669" : "#4f46e5";

    return {
      style: {
        backgroundColor: bg,
        color: "white",
        borderRadius: "0.375rem",
        padding: "4px 10px",
        fontSize: "0.875rem",
        fontWeight: 600,
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    };
  };
  const dayPropGetter = (date: Date) => {
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    return {
      style: {
        backgroundColor: isToday ? "#eff6ff" : isWeekend ? "#fef2f2" : "white",
        border: isToday ? "2px solid #3b82f6" : "1px solid #e5e7eb",
        color: isWeekend ? "#991b1b" : "#1f2937",
        fontWeight: isToday ? 600 : 400,
      },
    };
  };

  return (
    <div>
      <header className="bg-gray-50 dark:bg-darkbglight py-8">
        <div className="mx-8 max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-row items-center justify-between gap-4 flex-wrap">
          <h1 className="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:tracking-tight dark:text-gray-300">
            Calendar
          </h1>
          <div className="min-w-[320px] flex flex-col lg:flex-row gap-2 ">
            <div className="flex flex-col w-full">
              <label
                htmlFor="role"
                className="text-sm font-medium mb-1 dark:text-gray-200"
              >
                Role
              </label>
              <select
                id="role"
                className="p-1 border rounded w-full dark:text-gray-400 dark:bg-slate-800"
                value={selectedRole || ""}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">All</option>
                {roles.map((rs, index) => (
                  <option key={index} value={rs}>
                    {rs}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-full">
              <label
                htmlFor="interviewerEmail"
                className="text-sm font-medium mb-1 dark:text-gray-200"
              >
                Interviewer
              </label>
              <select
                id="interviewerEmail"
                className="p-1 border rounded w-full dark:text-gray-400 dark:bg-slate-800"
                value={selectedInterviewerEmail || ""}
                onChange={(e) => setSelectedInterviewerEmail(e.target.value)}
              >
                <option value="">All</option>
                {interviewerEmails.map((email, index) => (
                  <option key={index} value={email}>
                    {email}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-full">
              <label
                htmlFor="stage"
                className="text-sm font-medium mb-1 dark:text-gray-200"
              >
                Stage
              </label>
              <select
                id="stage"
                className="p-1 border rounded w-full dark:text-gray-400 dark:bg-slate-800"
                value={selectedStage || ""}
                onChange={(e) => setSelectedStage(e.target.value)}
              >
                <option value="">All</option>
                {stages.map((stage, index) => (
                  <option key={index} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>
      {isLoading ? (
        <div className="flex justify-center items-center h-80">
          <Spinner height="h-12" width="w-12" />
        </div>
      ) : (
        <div className="calendar-container px-6 py-4">
          <BigCalendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            date={intialDate}
            onNavigate={(newDate) => setInitialDate(newDate)}
            style={{ height: "80vh" }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventClick}
            className="ml-4 mr-4 rounded-lg shadow-lg dark:text-white dark:bg-darkbglight"
            defaultView="month"
            popup
            dayPropGetter={dayPropGetter}
            onRangeChange={handleRangeChange}
          />
        </div>
      )}

      {selectedEvent && (
        <ConfirmModal
          type="info"
          title="Are you sure you want to join this meeting?"
          open={confirmation}
          setOpen={setConfirmation}
          description={
            <div>
              <p className="mt-2 text-sm font-semibold">
                Event:{" "}
                {selectedEvent.candidateId ? (
                  <>
                    Interview with{" "}
                    <Link
                      to={`/roles/${selectedEvent.roleId}/candidates/${selectedEvent.candidateId}`}
                      className="text-blue-500 text-base hover:underline"
                    >
                      {selectedEvent.candidateName}
                    </Link>{" "}
                    for{" "}
                    <Link
                      to={`/roles/${selectedEvent.roleId}`}
                      className="text-blue-500 text-base hover:underline"
                    >
                      {selectedEvent.roleName}
                    </Link>
                  </>
                ) : (
                  <>
                    Open Slot for{" "}
                    <Link
                      to={`/roles/${selectedEvent.roleId}`}
                      className="text-blue-500  text-base hover:underline"
                    >
                      {selectedEvent.roleName}
                    </Link>
                  </>
                )}
              </p>
              <p className="mt-2 text-sm">
                Time:{" "}
                {`${new Date(selectedEvent.start).toLocaleDateString(
                  undefined,
                  {
                    weekday: "short",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  }
                )} at ${new Date(selectedEvent.start).toLocaleTimeString(
                  undefined,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )} - ${new Date(selectedEvent.end).toLocaleTimeString(
                  undefined,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}`}
              </p>
            </div>
          }
          confirmButtonTitle="Join"
          onConfirm={handleJoinMeeting}
          onCancel={() => setConfirmation(false)}
        />
      )}
    </div>
  );
};

export default InterviewCalendar;
